import React, { useEffect, useState } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Contact Us page. Pulls live values from business_info where available
// (business_name, business_email, business_phone, business_address) so the
// page reflects whatever admins entered during setup. Admin-editable rich
// markdown intro lives at business_info.contact_us_content and renders above
// the auto-generated email / phone / address card when present.
const ContactUs = () => {
  const [info, setInfo] = useState({
    business_name: '',
    business_email: '',
    business_phone: '',
    business_address: '',
  });
  const [adminContent, setAdminContent] = useState('');

  useEffect(() => {
    const keys = ['business_name', 'business_email', 'business_phone', 'business_address', 'contact_us_content'];
    Promise.all(keys.map(k =>
      axios.get(`${API_URL}/business-info/${k}`)
        .then(res => [k, res.data?.info_value || ''])
        .catch(() => [k, ''])
    )).then(pairs => {
      const map = Object.fromEntries(pairs);
      setInfo(prev => ({
        ...prev,
        business_name: map.business_name || '',
        business_email: map.business_email || '',
        business_phone: map.business_phone || '',
        business_address: map.business_address || '',
      }));
      setAdminContent(map.contact_us_content || '');
    });
  }, []);

  const hasAdminContent = adminContent.trim().length > 0;

  const hasAny = info.business_email || info.business_phone || info.business_address;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEOHead
        title="Contact Us"
        description={`Get in touch with ${info.business_name || 'us'} — phone, email, and address details.`}
      />
      <div className="max-w-3xl mx-auto px-4">
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <Mail className="text-primary-600" size={32} />
            <h1 className="text-3xl font-bold text-gray-900">Contact Us</h1>
          </div>

          {hasAdminContent ? (
            <div className="prose max-w-none text-gray-700 mb-6">
              <ReactMarkdown>{adminContent}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-gray-700 mb-6">
              {info.business_name
                ? `Reach out to ${info.business_name} any time.`
                : 'We would love to hear from you.'}
            </p>
          )}

          {hasAny ? (
            <div className="space-y-4">
              {info.business_email && (
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Mail className="text-primary-600 flex-shrink-0" size={20} />
                  <a
                    href={`mailto:${info.business_email}`}
                    className="text-primary-700 hover:underline break-all"
                  >
                    {info.business_email}
                  </a>
                </div>
              )}
              {info.business_phone && (
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Phone className="text-primary-600 flex-shrink-0" size={20} />
                  <a
                    href={`tel:${info.business_phone}`}
                    className="text-primary-700 hover:underline"
                  >
                    {info.business_phone}
                  </a>
                </div>
              )}
              {info.business_address && (
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <MapPin className="text-primary-600 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-gray-700 whitespace-pre-line">{info.business_address}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                No contact details have been configured yet. An admin can set the
                business email, phone, and address from the admin panel and they
                will appear here automatically.
              </p>
            </div>
          )}

          <p className="mt-8 text-sm text-gray-500 italic">
            Admins: customize the intro copy and add images to this page from
            Site Design &rarr; Static Page Content. The email / phone / address
            block above is auto-populated from your Business Info settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
